import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import {
  onAuthStateChanged,
  onIdTokenChanged,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut,
  type User,
} from 'firebase/auth'
import { auth, persistenzBereit } from './firebase'
import { ApiFehler, hole, setzeTokenQuelle } from './api'

/**
 * Anmeldung und Berechtigung.
 *
 * Firebase sagt nur, **wer** jemand ist. Ob er hier etwas darf, sagt
 * ausschließlich das Backend über `/api/ich` — der Browser entscheidet das
 * nie selbst (§5.2).
 */

export interface Profil {
  firebase_uid: string
  username: string | null
  display_name: string | null
  is_anonymous: boolean
  points: number
  profilbild: string | null
  profilbild_klein: string | null
}

export interface Ich {
  uid: string
  email: string | null
  rolle: 'admin' | 'moderator'
  profil: Profil | null
}

export type Zugang =
  | { art: 'laedt' }
  | { art: 'abgemeldet' }
  /** Angemeldet, aber das Backend hat noch nicht geantwortet. */
  | { art: 'pruefe'; nutzer: User }
  | { art: 'berechtigt'; nutzer: User; ich: Ich }
  | { art: 'verweigert'; nutzer: User; meldung: string }
  | { art: 'gestoert'; nutzer: User; meldung: string }

interface AuthWert {
  zugang: Zugang
  anmelden: (email: string, passwort: string) => Promise<void>
  abmelden: () => Promise<void>
  passwortZuruecksetzen: (email: string) => Promise<void>
  erneutPruefen: () => void
}

const AuthKontext = createContext<AuthWert | undefined>(undefined)

export function AuthAnbieter({ children }: { children: ReactNode }) {
  const [zugang, setZugang] = useState<Zugang>({ art: 'laedt' })
  const [runde, setRunde] = useState(0)

  // Jede API-Anfrage bekommt ein frisches ID-Token; das SDK erneuert es selbst.
  useEffect(() => {
    setzeTokenQuelle(async () => {
      const nutzer = auth.currentUser
      return nutzer ? nutzer.getIdToken() : null
    })
    return () => setzeTokenQuelle(null)
  }, [])

  useEffect(() => {
    let abgemeldet = false
    void persistenzBereit
    const stopp = onAuthStateChanged(auth, (nutzer) => {
      if (abgemeldet) return
      setZugang(nutzer ? { art: 'pruefe', nutzer } : { art: 'abgemeldet' })
    })
    return () => {
      abgemeldet = true
      stopp()
    }
  }, [])

  // Wird das Token erneuert, prüfen wir die Berechtigung erneut mit.
  useEffect(() => onIdTokenChanged(auth, () => setRunde((r) => r + 1)), [])

  const nutzer = 'nutzer' in zugang ? zugang.nutzer : undefined
  const uid = nutzer?.uid

  useEffect(() => {
    if (!uid || !nutzer) return
    let abgebrochen = false

    hole<Ich>('/api/ich')
      .then((ich) => {
        if (!abgebrochen) setZugang({ art: 'berechtigt', nutzer, ich })
      })
      .catch((fehler: unknown) => {
        if (abgebrochen) return
        if (fehler instanceof ApiFehler && fehler.status === 403) {
          setZugang({ art: 'verweigert', nutzer, meldung: fehler.message })
        } else {
          setZugang({
            art: 'gestoert',
            nutzer,
            meldung:
              fehler instanceof Error ? fehler.message : 'Unbekannter Fehler',
          })
        }
      })

    return () => {
      abgebrochen = true
    }
    // `runde` erzwingt die erneute Prüfung nach einem Tokenwechsel.

  }, [uid, runde])

  const wert = useMemo<AuthWert>(
    () => ({
      zugang,
      anmelden: async (email, passwort) => {
        await persistenzBereit
        await signInWithEmailAndPassword(auth, email, passwort)
      },
      abmelden: async () => {
        await signOut(auth)
      },
      passwortZuruecksetzen: async (email) => {
        await sendPasswordResetEmail(auth, email)
      },
      erneutPruefen: () => setRunde((r) => r + 1),
    }),
    [zugang],
  )

  return <AuthKontext.Provider value={wert}>{children}</AuthKontext.Provider>
}

export function useAuth(): AuthWert {
  const wert = useContext(AuthKontext)
  if (!wert) throw new Error('useAuth außerhalb von <AuthAnbieter>')
  return wert
}

/** Der angemeldete und berechtigte Mensch — nur innerhalb des Rahmens gültig. */
export function useIch(): Ich {
  const { zugang } = useAuth()
  if (zugang.art !== 'berechtigt') {
    throw new Error('useIch() ohne bestätigte Berechtigung')
  }
  return zugang.ich
}

/** Firebase-Fehlercodes in Sätze, die einem Menschen etwas sagen. */
export function anmeldeFehlerText(fehler: unknown): string {
  const code =
    typeof fehler === 'object' && fehler !== null && 'code' in fehler
      ? String((fehler as { code: unknown }).code)
      : ''

  switch (code) {
    case 'auth/invalid-email':
      return 'Diese E-Mail-Adresse ist nicht gültig.'
    case 'auth/user-disabled':
      return 'Dieses Konto ist gesperrt.'
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
      return 'E-Mail-Adresse oder Passwort stimmen nicht.'
    case 'auth/too-many-requests':
      return 'Zu viele Versuche. Bitte später erneut oder Passwort zurücksetzen.'
    case 'auth/network-request-failed':
      return 'Keine Verbindung. Ist das Netz da?'
    default:
      return fehler instanceof Error
        ? fehler.message
        : 'Die Anmeldung ist fehlgeschlagen.'
  }
}
